import { useState, useMemo } from 'react';
import { configToWagtail } from '../../utils/configToWagtail';
import { wagtailToConfig } from '../../utils/wagtailToConfig';
import s from './JSONExporter.module.scss';

const TABS = {
    WAGTAIL: 'wagtail',
    BUILDER: 'builder',
};

const JSONExporter = ({ config, onImport }) => {
    const [activeTab, setActiveTab] = useState(TABS.WAGTAIL);
    const [importText, setImportText] = useState('');
    const [copySuccess, setCopySuccess] = useState(false);
    const [importResult, setImportResult] = useState(null);

    const wagtailJson = useMemo(() => {
        const result = configToWagtail(config);
        if (result.success) {
            return JSON.stringify(result.json, null, 2);
        }
        return `// Error: ${result.error}`;
    }, [config]);

    const builderJson = useMemo(() => {
        return JSON.stringify(config, null, 2);
    }, [config]);

    const displayJson = activeTab === TABS.WAGTAIL ? wagtailJson : builderJson;

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(displayJson);
            setCopySuccess(true);
            setTimeout(() => setCopySuccess(false), 2000);
        } catch (err) {
            console.error('Failed to copy:', err);
        }
    };

    const handleImport = () => {
        setImportResult(null);

        if (!importText.trim()) {
            setImportResult({
                success: false,
                errors: ['Please enter JSON to import'],
            });
            return;
        }

        let parsed;
        try {
            parsed = JSON.parse(importText);
        } catch (e) {
            setImportResult({
                success: false,
                errors: [`Invalid JSON: ${e.message}`],
            });
            return;
        }

        // Detect format: Wagtail has "type" at root, Builder has "chartType"
        const isWagtailFormat = parsed.type && !parsed.chartType;
        const isBuilderFormat = parsed.chartType;

        if (isWagtailFormat) {
            const result = wagtailToConfig(parsed);
            if (result.success) {
                onImport(result.config);
                setImportResult({
                    success: true,
                    message: 'Imported from Wagtail JSON',
                    warnings: result.warnings,
                });
                setImportText('');
            } else {
                setImportResult(result);
            }
        } else if (isBuilderFormat) {
            // Direct builder format - validate minimally
            if (!parsed.series || !Array.isArray(parsed.series)) {
                setImportResult({
                    success: false,
                    errors: ['Invalid builder config: missing series array'],
                });
                return;
            }
            onImport(parsed);
            setImportResult({
                success: true,
                message: 'Imported from Builder JSON',
            });
            setImportText('');
        } else {
            setImportResult({
                success: false,
                errors: [
                    'Unknown JSON format. Expected Wagtail JSON (with "type" field) or Builder JSON (with "chartType" field)',
                ],
            });
        }
    };

    return (
        <div className={s.Root}>
            <div className={s.Tabs}>
                <button
                    className={`${s.Tab} ${activeTab === TABS.WAGTAIL ? s.TabActive : ''}`}
                    onClick={() => setActiveTab(TABS.WAGTAIL)}>
                    Wagtail JSON
                </button>
                <button
                    className={`${s.Tab} ${activeTab === TABS.BUILDER ? s.TabActive : ''}`}
                    onClick={() => setActiveTab(TABS.BUILDER)}>
                    Builder JSON
                </button>
            </div>

            <div className={s.ExportSection}>
                <div className={s.SectionHeader}>
                    <span className={s.SectionLabel}>Export</span>
                    <button
                        className={`${s.CopyButton} ${copySuccess ? s.CopySuccess : ''}`}
                        onClick={handleCopy}>
                        {copySuccess ? 'Copied!' : 'Copy to Clipboard'}
                    </button>
                </div>
                <textarea
                    className={s.JsonTextarea}
                    value={displayJson}
                    readOnly
                    rows={10}
                />
            </div>

            <div className={s.ImportSection}>
                <div className={s.SectionHeader}>
                    <span className={s.SectionLabel}>Import</span>
                </div>
                <textarea
                    className={s.JsonTextarea}
                    value={importText}
                    onChange={(e) => setImportText(e.target.value)}
                    placeholder="Paste Wagtail or Builder JSON here..."
                    rows={6}
                />
                <button
                    className={s.ImportButton}
                    onClick={handleImport}
                    disabled={!importText.trim()}>
                    Import
                </button>

                {importResult && (
                    <div
                        className={`${s.ImportResult} ${importResult.success ? s.Success : s.Error}`}>
                        {importResult.success ? (
                            <>
                                <span className={s.SuccessIcon}>&#10003;</span>
                                <span>{importResult.message}</span>
                            </>
                        ) : (
                            <>
                                <span className={s.ErrorIcon}>&#10005;</span>
                                <div className={s.ErrorList}>
                                    {importResult.errors?.map((err, i) => (
                                        <div key={i}>{err}</div>
                                    ))}
                                </div>
                            </>
                        )}
                        {importResult.warnings &&
                            importResult.warnings.length > 0 && (
                                <div className={s.WarningList}>
                                    <span className={s.WarningLabel}>
                                        Warnings:
                                    </span>
                                    {importResult.warnings.map((warn, i) => (
                                        <div key={i} className={s.Warning}>
                                            {warn}
                                        </div>
                                    ))}
                                </div>
                            )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default JSONExporter;
